import { useState, useEffect, useCallback } from 'react'
import {
  encryptData,
  decryptData,
  getAdminCryptoKey,
} from '../utils/adminCrypto'

export interface WebhookConfig {
  id: string
  name: string
  url: string
  active: boolean
}

const STORAGE_KEY = 'bvb_webhooks_enc'

// Default webhook (encrypted at runtime, not hardcoded in plain text)
const DEFAULT_WEBHOOK_ENCODED = btoa(
  JSON.stringify([
    {
      id: 'default',
      name: 'LetLois CRM',
      url: 'https://services.leadconnectorhq.com/hooks/WgeUwGrMjGHUoyOlMkdT/webhook-trigger/2cc8f807-35bf-4016-bfee-6cdd3150a7fc',
      active: true,
    },
  ]),
)

export function useWebhooks() {
  const [webhooks, setWebhooks] = useState<WebhookConfig[]>([])
  const [loading, setLoading] = useState(true)

  const loadWebhooks = useCallback(async () => {
    setLoading(true)
    try {
      const stored = localStorage.getItem(STORAGE_KEY)
      if (stored) {
        const decrypted = await decryptData(stored)
        setWebhooks(JSON.parse(decrypted))
      } else {
        // First time: load defaults (unencrypted seed, will be encrypted on first save)
        const defaults = JSON.parse(atob(DEFAULT_WEBHOOK_ENCODED))
        setWebhooks(defaults)
        await saveWebhooks(defaults)
      }
    } catch (e) {
      // If decryption fails (different key), start fresh
      const defaults = JSON.parse(atob(DEFAULT_WEBHOOK_ENCODED))
      setWebhooks(defaults)
    } finally {
      setLoading(false)
    }
  }, [])

  const saveWebhooks = async (list: WebhookConfig[]) => {
    try {
      const encrypted = await encryptData(JSON.stringify(list))
      localStorage.setItem(STORAGE_KEY, encrypted)
    } catch (e) {
      console.error('Failed to save webhooks', e)
    }
  }

  useEffect(() => {
    if (getAdminCryptoKey()) {
      loadWebhooks()
    }
  }, [loadWebhooks])

  const addWebhook = async (name: string, url: string) => {
    const newHook: WebhookConfig = {
      id: crypto.randomUUID(),
      name,
      url,
      active: true,
    }
    const updated = [...webhooks, newHook]
    setWebhooks(updated)
    await saveWebhooks(updated)
  }

  const updateWebhook = async (id: string, patch: Partial<WebhookConfig>) => {
    const updated = webhooks.map((w) => (w.id === id ? { ...w, ...patch } : w))
    setWebhooks(updated)
    await saveWebhooks(updated)
  }

  const deleteWebhook = async (id: string) => {
    const updated = webhooks.filter((w) => w.id !== id)
    setWebhooks(updated)
    await saveWebhooks(updated)
  }

  const getActiveWebhooks = () => webhooks.filter((w) => w.active)

  return {
    webhooks,
    loading,
    addWebhook,
    updateWebhook,
    deleteWebhook,
    getActiveWebhooks,
    reload: loadWebhooks,
  }
}

// Standalone function to send to all active webhooks (used outside hook context)
export async function sendToWebhooks(
  payload: Record<string, unknown>,
): Promise<void> {
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    let hooks: WebhookConfig[] = []
    if (stored) {
      try {
        const decrypted = await decryptData(stored)
        hooks = JSON.parse(decrypted)
      } catch {
        // If can't decrypt (no admin key), use the default
        hooks = JSON.parse(atob(DEFAULT_WEBHOOK_ENCODED))
      }
    } else {
      hooks = JSON.parse(atob(DEFAULT_WEBHOOK_ENCODED))
    }

    const active = hooks.filter((h) => h.active)
    await Promise.allSettled(
      active.map((hook) =>
        fetch(hook.url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        }),
      ),
    )
  } catch (e) {
    console.error('Webhook send failed', e)
  }
}
